import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { useTabParam } from "../../hooks/useTabParam";
import DropdownSelect from "../../components/DropdownSelect";
import type { EmployeeSummary, Site } from "../../types";
import EmployeeSearch from "../../components/EmployeeSearch";

interface Template { id: number; name: string; region_id: number | null; site_id: number | null; is_offboarding: boolean; }
interface Task { id: number; title: string; assigned_role: string; category: string; due_days: number; is_required: boolean; sort_order: number; }
interface ActiveOB { id: number; employee_id: number; started_at: string; }
interface ActiveOBTask { id: number; title: string; status: string; due_date: string; completed_at: string | null; notes: string | null; }

const ONBOARDING_TABS = ["active", "templates"] as const;

export default function AdminOnboardingPage() {
  const [dashboard, setDashboard] = useState<{ active_count: number; onboardings: ActiveOB[] } | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [employees, setEmployees] = useState<EmployeeSummary[]>([]);
  const [tab, setTab] = useTabParam(ONBOARDING_TABS, "active");
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [showStartForm, setShowStartForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [templateTasks, setTemplateTasks] = useState<Task[]>([]);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [selectedOB, setSelectedOB] = useState<number | null>(null);
  const [obTasks, setObTasks] = useState<ActiveOBTask[]>([]);
  const [obTasksLoading, setObTasksLoading] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const [d, t, s, e] = await Promise.all([
        api.get<{ active_count: number; onboardings: ActiveOB[] }>("/api/onboarding/dashboard"),
        api.get<Template[]>("/api/onboarding/templates"),
        api.get<Site[]>("/api/sites"),
        api.get<EmployeeSummary[]>("/api/employees"),
      ]);
      setDashboard(d); setTemplates(t); setSites(s); setEmployees(e);
    } catch {}
  }, []);
  useEffect(() => { void load(); }, [load]);

  const loadTasks = async (tid: number) => {
    try {
      const d = await api.get<{ tasks: Task[] }>(`/api/onboarding/templates/${tid}`);
      setTemplateTasks(d.tasks);
    } catch {}
  };

  const loadObTasks = async (obId: number) => {
    setObTasksLoading(true);
    try {
      const d = await api.get<{ tasks: ActiveOBTask[] }>(`/api/onboarding/${obId}`);
      setObTasks(d.tasks);
    } catch {
      setObTasks([]);
    } finally {
      setObTasksLoading(false);
    }
  };

  const selectOB = (id: number) => {
    if (selectedOB === id) { setSelectedOB(null); } else { setSelectedOB(id); void loadObTasks(id); }
  };

  const completeObTask = async (taskId: number, obId: number) => {
    setCompletingTaskId(taskId);
    try {
      await api.put(`/api/onboarding/tasks/${taskId}/complete`, {});
      await loadObTasks(obId);
    } catch {
      /* silently fail */
    } finally {
      setCompletingTaskId(null);
    }
  };

  const selectTemplate = (id: number) => {
    if (selectedTemplate === id) { setSelectedTemplate(null); } else { setSelectedTemplate(id); void loadTasks(id); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Onboarding</h1>
        <div className="flex gap-2">
          <button type="button" onClick={() => setShowStartForm(true)} className="rounded-lg border border-pme-blue px-4 py-2 text-sm font-semibold text-pme-blue">Start Onboarding</button>
          <button type="button" onClick={() => setShowTemplateForm(true)} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">+ Create Template</button>
        </div>
      </div>

      <nav className="flex gap-1 border-b border-gray-200">
        <TabBtn active={tab === "active"} onClick={() => setTab("active")}>Active ({dashboard?.active_count ?? 0})</TabBtn>
        <TabBtn active={tab === "templates"} onClick={() => setTab("templates")}>Templates ({templates.length})</TabBtn>
      </nav>

      {showTemplateForm && <TemplateForm sites={sites} onSaved={() => { setShowTemplateForm(false); void load(); }} onCancel={() => setShowTemplateForm(false)} />}
      {showStartForm && <StartForm templates={templates} onDone={() => { setShowStartForm(false); void load(); }} onCancel={() => setShowStartForm(false)} />}

      {tab === "active" && (
        <div className="space-y-2">
          {(dashboard?.onboardings ?? []).map((o) => {
            const emp = employees.find((e) => e.id === o.employee_id);
            return (
              <div key={o.id} className="rounded-xl border border-gray-200 bg-white shadow-sm">
                <button type="button" onClick={() => selectOB(o.id)} className="flex w-full items-center justify-between px-5 py-4 text-left">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{emp ? `${emp.first_name} ${emp.last_name}` : `Employee #${o.employee_id}`}</p>
                    <p className="text-xs text-gray-500">Started: {new Date(o.started_at).toLocaleDateString()}</p>
                  </div>
                  <span className="text-gray-400">{selectedOB === o.id ? "▾" : "▸"}</span>
                </button>
                {selectedOB === o.id && (
                  <div className="border-t border-gray-100 px-5 py-4 space-y-2">
                    <h4 className="text-xs font-semibold uppercase text-gray-500">Tasks ({obTasks.length})</h4>
                    {obTasksLoading && <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />}
                    {!obTasksLoading && obTasks.length === 0 && <p className="text-xs text-gray-400">No tasks.</p>}
                    {!obTasksLoading && obTasks.map((tk) => (
                      <div key={tk.id} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            {tk.status === "completed" && (
                              <svg className="h-4 w-4 shrink-0 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                            <p className="text-sm font-medium text-gray-900">{tk.title}</p>
                          </div>
                          <p className="text-xs text-gray-500">
                            Due: {new Date(tk.due_date).toLocaleDateString()}
                            {tk.completed_at && <span className="ml-2">Completed: {new Date(tk.completed_at).toLocaleDateString()}</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                            tk.status === "completed" ? "bg-green-50 text-green-700" :
                            tk.status === "in_progress" ? "bg-blue-50 text-blue-700" :
                            tk.status === "overdue" ? "bg-red-50 text-red-700" :
                            tk.status === "skipped" ? "bg-gray-100 text-gray-500" :
                            "bg-gray-100 text-gray-600"
                          }`}>
                            {tk.status.replace(/_/g, " ")}
                          </span>
                          {(tk.status === "pending" || tk.status === "in_progress" || tk.status === "overdue") && (
                            <button
                              type="button"
                              onClick={() => completeObTask(tk.id, o.id)}
                              disabled={completingTaskId === tk.id}
                              className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                            >
                              {completingTaskId === tk.id ? "..." : "Complete"}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {(dashboard?.onboardings ?? []).length === 0 && <p className="text-sm text-gray-400">No active onboardings.</p>}
        </div>
      )}

      {tab === "templates" && (
        <div className="space-y-2">
          {templates.map((t) => (
            <div key={t.id} className="rounded-xl border border-gray-200 bg-white shadow-sm">
              <button type="button" onClick={() => selectTemplate(t.id)} className="flex w-full items-center justify-between px-5 py-4 text-left">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                  <p className="text-xs text-gray-500">{t.is_offboarding ? "Offboarding" : "Onboarding"}</p>
                </div>
                <span className="text-gray-400">{selectedTemplate === t.id ? "▾" : "▸"}</span>
              </button>
              {selectedTemplate === t.id && (
                <div className="border-t border-gray-100 px-5 py-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-semibold uppercase text-gray-500">Tasks ({templateTasks.length})</h4>
                    <button type="button" onClick={() => setShowTaskForm(true)} className="rounded border border-gray-300 px-2 py-1 text-xs">+ Add Task</button>
                  </div>
                  {showTaskForm && <TaskForm templateId={t.id} onSaved={() => { setShowTaskForm(false); void loadTasks(t.id); }} onCancel={() => setShowTaskForm(false)} />}
                  {templateTasks.map((tk) => (
                    <div key={tk.id} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{tk.title}</p>
                        <p className="text-xs text-gray-500">{tk.assigned_role} · {tk.category} · Day {tk.due_days}{tk.is_required ? " · Required" : ""}</p>
                      </div>
                    </div>
                  ))}
                  {templateTasks.length === 0 && !showTaskForm && <p className="text-xs text-gray-400">No tasks yet.</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateForm({ sites, onSaved, onCancel }: { sites: Site[]; onSaved: () => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [isOffboarding, setIsOffboarding] = useState(false);
  const [siteId, setSiteId] = useState<string>("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSubmitting(true);
    try { await api.post("/api/onboarding/templates", { name, description: description || null, is_offboarding: isOffboarding, site_id: siteId ? Number(siteId) : null }); onSaved(); } catch {} finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">New Template</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Name</span><input value={name} onChange={(e) => setName(e.target.value)} required placeholder="UK Warehouse Onboarding" className="input mt-1" /></label>
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Site (optional)</span>
          <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className="input mt-1"><option value="">All sites</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        </label>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={isOffboarding} onChange={(e) => setIsOffboarding(e.target.checked)} /> Offboarding template</label>
      <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Description</span><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="input mt-1" /></label>
      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{submitting ? "Saving..." : "Save"}</button>
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}

function TaskForm({ templateId, onSaved, onCancel }: { templateId: number; onSaved: () => void; onCancel: () => void }) {
  const [title, setTitle] = useState("");
  const [assignedRole, setAssignedRole] = useState("employee");
  const [category, setCategory] = useState("other");
  const [dueDays, setDueDays] = useState(0);
  const [required, setRequired] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSubmitting(true);
    try { await api.post(`/api/onboarding/templates/${templateId}/tasks`, { title, assigned_role: assignedRole, category, due_days: dueDays, is_required: required }); onSaved(); } catch {} finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
      <label className="block text-xs"><span className="font-medium text-gray-700">Task Title</span><input value={title} onChange={(e) => setTitle(e.target.value)} required className="input mt-1" /></label>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
        <label className="block text-xs"><span className="font-medium text-gray-700">Assigned To</span>
          <DropdownSelect listCode="onboarding_role" value={assignedRole} onChange={setAssignedRole} className="input mt-1" />
        </label>
        <label className="block text-xs"><span className="font-medium text-gray-700">Category</span>
          <DropdownSelect listCode="onboarding_category" value={category} onChange={setCategory} className="input mt-1" />
        </label>
        <label className="block text-xs"><span className="font-medium text-gray-700">Due (days)</span><input type="number" min={0} value={dueDays} onChange={(e) => setDueDays(Number(e.target.value))} className="input mt-1" /></label>
        <label className="flex items-center gap-1 text-xs pt-4"><input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} /> Required</label>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="rounded bg-pme-blue px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">{submitting ? "Saving..." : "Add Task"}</button>
        <button type="button" onClick={onCancel} className="rounded border border-gray-300 px-3 py-1 text-xs">Cancel</button>
      </div>
    </form>
  );
}

function StartForm({ templates, onDone, onCancel }: { templates: Template[]; onDone: () => void; onCancel: () => void }) {
  const [empId, setEmpId] = useState<string>("");
  const [tempId, setTempId] = useState<string>(templates[0]?.id?.toString() ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const r = await api.post<{ id: number; task_count: number }>("/api/onboarding/start", { employee_id: Number(empId), template_id: Number(tempId) });
      setResult(`Onboarding started with ${r.task_count} tasks`);
    } catch {} finally { setSubmitting(false); }
  };

  if (result) return (
    <div className="rounded-xl border border-green-300 bg-green-50 p-4 text-sm text-green-800">
      <p>{result}</p>
      <button type="button" onClick={onDone} className="mt-2 text-xs font-semibold text-pme-blue">Done</button>
    </div>
  );

  return (
    <form onSubmit={submit} className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">Start Onboarding</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Employee</span>
          <EmployeeSearch
            value={empId ? Number(empId) : null}
            onChange={(v) => setEmpId(v ? String(v) : "")}
            placeholder="Select employee..."
            className="input mt-1"
          />
        </label>
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Template</span>
          <select value={tempId} onChange={(e) => setTempId(e.target.value)} required className="input mt-1">{templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
        </label>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{submitting ? "Starting..." : "Start"}</button>
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button type="button" onClick={onClick} className={["px-4 py-2 text-sm font-semibold", active ? "border-b-2 border-pme-blue text-pme-blue" : "text-gray-500"].join(" ")}>{children}</button>;
}
